import { Controller } from "@hotwired/stimulus"

// Live character / segment counter for the course SMS reminder field, plus a
// confirmation step on save.
//
// Twilio bills per segment: 160 characters for a single-segment message, then
// 153 per segment once it splits. Placeholders like {name} are replaced with
// real values before sending, so the count here is an estimate -- we substitute
// representative sample values to keep it honest.
export default class extends Controller {
  static targets = ["input", "output"]
  static values = {
    samples: { type: Object, default: {} }
  }

  connect() {
    this.update()

    // Confirm on save so nobody ships a costly or malformed message by accident.
    this.form = this.element.closest("form")
    if (this.form) {
      this.onSubmit = this.confirmSave.bind(this)
      this.form.addEventListener("submit", this.onSubmit)
    }
  }

  disconnect() {
    if (this.form && this.onSubmit) {
      this.form.removeEventListener("submit", this.onSubmit)
    }
  }

  update() {
    const { chars, segments } = this.measure()

    this.outputTarget.textContent =
      `≈ ${chars} characters · ${segments} SMS segment${segments === 1 ? "" : "s"}`

    // Twilio charges per segment, so flag anything over one.
    this.outputTarget.className =
      segments <= 1
        ? "text-xs font-medium text-emerald-600 mt-2"
        : segments <= 2
          ? "text-xs font-medium text-amber-600 mt-2"
          : "text-xs font-medium text-red-600 mt-2"
  }

  // Show the admin exactly what a student will receive before the form saves.
  confirmSave(event) {
    const { chars, segments } = this.measure()

    let message =
      "This text message will be sent to every student booked on this course:\n\n" +
      `${this.preview()}\n\n` +
      `${chars} characters · ${segments} SMS segment${segments === 1 ? "" : "s"} per student.`

    if (segments > 1) {
      message += `\n\nWarning: over one segment, so Twilio bills ${segments}x per student.`
    }

    message += "\n\nSave these changes?"

    if (!window.confirm(message)) {
      event.preventDefault()
      event.stopImmediatePropagation()
    }
  }

  measure() {
    const chars = this.preview().length
    const segments = chars === 0 ? 0 : chars <= 160 ? 1 : Math.ceil(chars / 153)
    return { chars, segments }
  }

  // Substitute sample values so the estimate reflects a real message.
  preview() {
    const samples = this.samplesValue
    return this.inputTarget.value.replace(/\{(\w+)\}/g, (match, key) =>
      Object.prototype.hasOwnProperty.call(samples, key) ? samples[key] : match
    )
  }
}
